import {MetricsWrapper} from '../../components/MetricsWrapper';
import {EventStatus, IncidentDetailPage, LandingPage} from '../../constants';
import ErrorBoundary from '../../components/ErrorBoundary';
import {postAnalytics} from '../../lib/analytics/postAnalytics';
import '../../assets/sass/landingPage.scss';
import {useEffect} from 'react';
import {
  isCustomBrandedRealm,
  consoleBaseURLs,
  hostnameToRealm,
} from '../../urlUtils';
import urlJoin from 'url-join';
import {UXConfig} from '../../model/brandingModel';
import {AppDispatch, useAppDispatch} from '../../store';
import {
  headerLogoAltTextSlice,
  headerLogoSlice,
  headerLogoHeadingSlice,
  isBrandingAppliedSlice,
  footerLeftMarkdownSlice,
  footerRightMarkdownSlice,
  faviconSlice,
} from '../../reducers/brandingReducers';

interface LandingContainerProps {}

const handleError = () => {
  postAnalytics(IncidentDetailPage.render, EventStatus.Failure);
};

function sanitizePath(path: string) {
  if (path.startsWith('/')) {
    return path.substring(1); // Start from the second character
  }
  return path;
}

async function applyTheme(
  uxConfig: UXConfig,
  uxConfigBaseURL: string,
  dispatch: AppDispatch,
) {
  let selectedTheme = uxConfig.themes[0];
  for (const theme of uxConfig.themes) {
    if (theme.id === uxConfig.realmDefaultTheme) {
      selectedTheme = theme;
    }
  }

  // Favicon
  const favicon = uxConfig.commonAssets.favicon;
  if (favicon) {
    dispatch(
      faviconSlice.actions.updateFavicon(urlJoin(uxConfigBaseURL, favicon)),
    );
    dispatch(
      faviconSlice.actions.updateAppleTouchFaviconValue(
        urlJoin(uxConfigBaseURL, favicon),
      ),
    );
  }

  // Header
  if (selectedTheme.assets.logo !== undefined) {
    dispatch(
      headerLogoSlice.actions.updateHeaderLogo(
        urlJoin(uxConfigBaseURL, selectedTheme.assets.logo),
      ),
    );
  }
  dispatch(
    headerLogoAltTextSlice.actions.updateHeaderLogoAltText(
      uxConfig['company-name']?.companyName ?? uxConfig.pageconfig.title ?? '',
    ),
  );
  dispatch(
    headerLogoHeadingSlice.actions.updateHeaderLogoHeading(
      uxConfig['company-name']?.companyName ?? uxConfig.pageconfig.title ?? '',
    ),
  );

  // Footer
  const footerLeft = uxConfig.commonAssets['footer-left'];
  if (footerLeft !== undefined) {
    const resp = await fetch(urlJoin(uxConfigBaseURL, footerLeft));
    dispatch(
      footerLeftMarkdownSlice.actions.updateFooterLeft(
        await resp.text().then((value) => value.replace(/['"]+/g, '')),
      ),
    );
  }
  const footerRight = uxConfig.commonAssets['footer-right'];
  if (footerRight !== undefined) {
    const resp = await fetch(urlJoin(uxConfigBaseURL, footerRight));
    dispatch(
      footerRightMarkdownSlice.actions.updateFooterRight(
        await resp.text().then((value) => value.replace(/['"]+/g, '')),
      ),
    );
  }
}

async function fetchAndApplyUXConfig(dispatch: AppDispatch): Promise<void> {
  try {
    const realm = hostnameToRealm.get(window.location.hostname);
    if (realm === undefined) {
      throw new Error(
        "Unknown Realm for hostname '" + window.location.hostname + "'",
      );
    }
    const realmBaseURL = urlJoin(
      consoleBaseURLs.get(realm) ?? '',
      'plugins/ux-config/console-home',
    );
    const uxConfigResponse = await fetch(
      urlJoin(realmBaseURL, 'ux-config.json'),
    );
    if (!uxConfigResponse.ok) {
      return;
    }
    const uxConfig: UXConfig = await uxConfigResponse.json();
    applyTheme(uxConfig, realmBaseURL, dispatch);
  } catch (error) {
    console.error('Unable to fetch/apply UX config', error);
  }
}

const LandingContainer = (props: LandingContainerProps) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (isCustomBrandedRealm(window.location.hostname)) {
      fetchAndApplyUXConfig(dispatch).then((value) => {
        dispatch(isBrandingAppliedSlice.actions.updateIsBrandingApplied(true));
      });
    } else {
      console.warn('Landing page loaded in a non custom branded realm');
    }
  }, []); // Empty array

  console.log('Rendering LandingContainer');
  return (
    <ErrorBoundary onError={handleError}>
      <MetricsWrapper
        eventName={LandingPage.load}
        eventStatus={EventStatus.Success}>
        <div className="spinnerContainer">
          <div className="spinner"></div>
        </div>
      </MetricsWrapper>
      ;
    </ErrorBoundary>
  );
};

export default LandingContainer;
